import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { CheckCircle2, GraduationCap, Loader2, Lock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const EMPTY = {
  nombre: '', apellidos: '', documento_identidad: '', fecha_nacimiento: '',
  email: '', telefono: '', categoria_profesional: '', centro_trabajo: '',
};

const PreinscriptionForm = () => {
  const { courseType, courseId } = useParams();
  const [info, setInfo] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [privacy, setPrivacy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/preinscriptions/course-info/${courseType}/${courseId}`)
      .then((r) => setInfo(r.data))
      .catch(() => setNotFound(true));
  }, [courseType, courseId]);

  const set = (k, v) => { if (error) setError(''); setForm((f) => ({ ...f, [k]: v })); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const required = ['nombre', 'apellidos', 'documento_identidad', 'fecha_nacimiento', 'email', 'telefono', 'categoria_profesional', 'centro_trabajo'];
    for (const k of required) {
      if (!String(form[k] || '').trim()) { setError('Por favor, rellena todos los campos.'); return; }
    }
    if (!privacy) { setError('Debes aceptar la política de privacidad para continuar.'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/preinscriptions/public`, { course_id: courseId, course_type: courseType, privacy_accepted: true, ...form });
      setDone(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'No se pudo enviar la preinscripción. Inténtalo de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center max-w-md">
          <h1 className="text-xl font-semibold text-slate-800">Formación no encontrada</h1>
          <p className="text-slate-500 mt-2">El enlace de preinscripción no es válido o la formación ya no está disponible.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#004E89] to-[#00386a] py-10 px-4">
      <div className="max-w-xl mx-auto">
        <div className="flex items-center gap-2 text-white mb-6">
          <GraduationCap className="w-7 h-7" />
          <div>
            <p className="font-bold text-lg leading-tight">IRTA</p>
            <p className="text-xs text-white/70 -mt-0.5">Healthcare Training</p>
          </div>
        </div>

        {done ? (
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center" data-testid="preinscription-success">
            <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-slate-800">¡Preinscripción enviada!</h1>
            <p className="text-slate-500 mt-3">
              Hemos recibido tu solicitud para <strong>{info?.course_name}</strong>. El director de la formación la revisará y,
              si es aceptada, recibirás un correo con las instrucciones de acceso.
            </p>
          </div>
        ) : info && info.open === false ? (
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center" data-testid="preinscription-closed">
            <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-7 h-7 text-slate-400" />
            </div>
            <h1 className="text-2xl font-bold text-slate-800">Preinscripciones cerradas</h1>
            <p className="text-slate-500 mt-3">{info.closed_reason || 'En este momento no se admiten nuevas preinscripciones para esta formación.'}</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="px-6 py-5 border-b border-slate-100">
              <p className="text-xs uppercase tracking-wide text-[#004E89] font-semibold">Formulario de preinscripción</p>
              <h1 className="text-xl font-bold text-slate-800 mt-1" data-testid="preinscription-course-name">
                {info ? info.course_name : <Loader2 className="w-5 h-5 animate-spin inline" />}
              </h1>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4" data-testid="preinscription-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm">Nombre</Label>
                  <Input value={form.nombre} onChange={(e) => set('nombre', e.target.value)} data-testid="pre-nombre" />
                </div>
                <div>
                  <Label className="text-sm">Apellidos</Label>
                  <Input value={form.apellidos} onChange={(e) => set('apellidos', e.target.value)} data-testid="pre-apellidos" />
                </div>
                <div>
                  <Label className="text-sm">Documento de identidad</Label>
                  <Input value={form.documento_identidad} onChange={(e) => set('documento_identidad', e.target.value)} data-testid="pre-dni" />
                </div>
                <div>
                  <Label className="text-sm">Fecha de nacimiento</Label>
                  <Input type="date" value={form.fecha_nacimiento} onChange={(e) => set('fecha_nacimiento', e.target.value)} data-testid="pre-fecha" />
                </div>
                <div>
                  <Label className="text-sm">Correo electrónico</Label>
                  <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} data-testid="pre-email" />
                </div>
                <div>
                  <Label className="text-sm">Teléfono</Label>
                  <Input value={form.telefono} onChange={(e) => set('telefono', e.target.value)} data-testid="pre-telefono" />
                </div>
                <div>
                  <Label className="text-sm">Categoría profesional sanitaria</Label>
                  <Select value={form.categoria_profesional} onValueChange={(v) => set('categoria_profesional', v)}>
                    <SelectTrigger data-testid="pre-categoria"><SelectValue placeholder="Selecciona..." /></SelectTrigger>
                    <SelectContent>
                      {(info?.categorias || ['Médico/a', 'Enfermera/o', 'TES', 'TCAE', 'Otros']).map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm">Centro de trabajo</Label>
                  <Input value={form.centro_trabajo} onChange={(e) => set('centro_trabajo', e.target.value)} data-testid="pre-centro" />
                </div>
              </div>

              {error && <p className="text-sm text-red-600" data-testid="pre-error">{error}</p>}

              <div className="flex items-start gap-2.5 rounded-lg bg-slate-50 border border-slate-100 p-3">
                <Checkbox id="pre-privacy" checked={privacy} onCheckedChange={(v) => { if (error) setError(''); setPrivacy(!!v); }} className="mt-0.5" data-testid="pre-privacy" />
                <Label htmlFor="pre-privacy" className="text-xs text-slate-500 font-normal leading-relaxed cursor-pointer">
                  He leído y acepto la <strong className="text-slate-700">política de privacidad</strong>. Consiento el tratamiento de mis datos personales
                  (incluidos documento de identidad y fecha de nacimiento) con la única finalidad de gestionar mi inscripción en esta formación,
                  conforme al RGPD.
                </Label>
              </div>

              <Button type="submit" disabled={submitting} className="w-full bg-[#004E89] hover:bg-[#003B66]" data-testid="pre-submit">
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enviando...</> : 'Enviar preinscripción'}
              </Button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default PreinscriptionForm;
